import { Check, ClipboardCheck, Minus } from "lucide-react";
import { cn } from "./ui/utils";

export type LeaveHandoverSummaryData = {
  coverName: string | null;
  notes: string | null;
  checklist: { ticked: boolean }[];
};

// Short labels for the five handover checklist items, in
// Leave.handoverChk1..handoverChk5 order.
const HANDOVER_CHECKLIST_ITEMS = [
  { label: "Duties handed over and cover briefed", notApplicableWhenNoCover: true },
  { label: "Out-of-office reply and voicemail set", notApplicableWhenNoCover: false },
  { label: "Urgent jobs, deadlines and client issues assigned", notApplicableWhenNoCover: false },
  { label: "Calendar meetings delegated, rescheduled or declined", notApplicableWhenNoCover: true },
  { label: "No important issue left without an owner", notApplicableWhenNoCover: false }
];

/**
 * Read-only summary of a leave request's handover confirmation (cover person,
 * notes and checklist tick summary) for approval and attendance-history views.
 * Renders nothing for requests that carry no handover (Personal/Sick and
 * Unpaid leave, or requests lodged before the handover step existed).
 */
export function LeaveHandoverSummary({
  handover,
  variant = "card",
  className
}: {
  handover?: LeaveHandoverSummaryData | null;
  variant?: "card" | "plain";
  className?: string;
}) {
  if (!handover) return null;

  const notRequired = !handover.coverName;

  return (
    <div
      className={cn(
        variant === "card"
          ? "bg-blue-50/50 border border-blue-100 rounded-2xl p-4 space-y-3"
          : "pt-2 border-t border-gray-100 space-y-3",
        className
      )}
    >
      <div className="flex items-center gap-2 text-blue-800">
        <ClipboardCheck className="h-4 w-4" />
        <p className="text-[10px] font-black uppercase tracking-wider">Handover confirmation</p>
      </div>

      <div className="flex items-start justify-between gap-4">
        <span className="text-sm text-gray-500 font-medium">Person covering</span>
        <span className="text-sm font-bold text-gray-900 text-right">
          {handover.coverName || "Not required — no handover needed"}
        </span>
      </div>

      <div className="space-y-1.5">
        {HANDOVER_CHECKLIST_ITEMS.map((item, index) => {
          const notApplicable = item.notApplicableWhenNoCover && notRequired;
          const ticked = !!(handover.checklist[index] && handover.checklist[index].ticked);

          return (
            <div key={item.label} className="flex items-start justify-between gap-3">
              <span className="flex items-start gap-2">
                {notApplicable ? (
                  <Minus className="h-3.5 w-3.5 text-gray-400 shrink-0 mt-0.5" />
                ) : (
                  <Check className={cn("h-3.5 w-3.5 shrink-0 mt-0.5", ticked ? "text-green-600" : "text-amber-600")} />
                )}
                <span
                  className={cn(
                    "text-xs font-medium leading-relaxed",
                    ticked || notApplicable ? "text-gray-600" : "text-amber-700"
                  )}
                >
                  {item.label}
                </span>
              </span>
              <span
                className={cn(
                  "text-[10px] font-black uppercase tracking-wider shrink-0 mt-0.5",
                  notApplicable ? "text-gray-400" : ticked ? "text-green-600" : "text-amber-600"
                )}
              >
                {notApplicable ? "N/A" : ticked ? "Confirmed" : "Outstanding"}
              </span>
            </div>
          );
        })}
      </div>

      {handover.notes && (
        <div className="bg-white border border-blue-100 rounded-xl p-3">
          <p className="text-[10px] font-black uppercase tracking-wider text-gray-400 mb-1">Notes for cover</p>
          <p className="text-xs text-gray-700 font-medium leading-relaxed whitespace-pre-wrap">{handover.notes}</p>
        </div>
      )}
    </div>
  );
}
